import { useParams, useNavigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { TokenMetric, getTokenMetrics } from '../services/api';
import TokenAvatar from '../components/TokenAvatar';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Holder {
  address: string;
  owner: string;
  amount: number;
  delegated_amount?: number;
  total_amount?: number;
  label?: string;
  is_exchange?: boolean;
  percentage: number;
}

interface LorenzPoint {
  holders: number;
  holdings: number;
}

export default function TokenDetail() {
  const { mint } = useParams<{ mint: string }>();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState<TokenMetric | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [excludeExchanges, setExcludeExchanges] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!mint) return;
      
      try {
        setLoading(true);
        const data = await getTokenMetrics(mint, excludeExchanges);
        const typedData: TokenMetric = {
          ...data,
          price_history: data.price_history || []
        };
        
        
        
        
        setMetrics(typedData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch token metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [mint, excludeExchanges]);

  const formatPriceChange = (change: number | undefined | null) => {
    if (change === undefined || change === null) return 'N/A';
    const formatted = change.toFixed(2);
    return `${change >= 0 ? '+' : ''}${formatted}%`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-red-500">Error: {error}</div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div>No data available</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <nav className="border-b border-gray-800 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <button
              className="text-gray-300 hover:text-white px-4 py-2 rounded"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            <div className="flex gap-4">
              <Link 
                to="/signup"
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
              >
                Sign Up
              </Link>
              <Link 
                to="/login"
                className="text-gray-300 border border-gray-600 hover:text-white px-4 py-2 rounded"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            {metrics.image_url ? (
              <img 
                src={metrics.image_url}
                alt={`${metrics.symbol} logo`}
                className="w-12 h-12 rounded-full"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.onerror = null;
                  target.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center';
                  fallback.innerHTML = `<span class='text-lg text-gray-400'>${metrics.symbol[0]}</span>`;
                  if (target.parentNode) {
                    target.parentNode.insertBefore(fallback, target);
                  }
                }}
              />
            ) : (
              <div className='w-12 h-12 rounded-full bg-gray-700 flex items-center justify-center'>
                <span className='text-lg text-gray-400'>{metrics.symbol[0]}</span>
              </div>
            )}
            <div>
              <h1 className="text-4xl font-bold">{metrics.symbol}</h1>
              {metrics?.price_change_24h !== undefined && metrics?.price_change_24h !== null && (
                <p className={`text-lg ${metrics.price_change_24h >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  24h Change: {formatPriceChange(metrics.price_change_24h)}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-4 bg-gray-800 rounded-lg p-3">
            <input
              type="checkbox"
              id="exclude-exchanges"
              checked={excludeExchanges}
              onChange={(e) => setExcludeExchanges(e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="exclude-exchanges" className="text-gray-300">
              Exclude Exchanges
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">HHI</h3>
            <p className="text-3xl font-bold text-white">{metrics.hhi.toFixed(2)}</p>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Gini Coefficient</h3>
            <p className="text-3xl font-bold text-white">{metrics.gini_coefficient.toFixed(4)}</p>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Total Holders</h3>
            <p className="text-3xl font-bold text-white">{metrics.total_holders.toLocaleString()}</p>
          </div>
        </div>

        {/* Supply Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Total Supply</h3>
            <p className="text-3xl font-bold text-white">
              {metrics.total_supply && metrics.decimals ? 
                (metrics.total_supply / Math.pow(10, metrics.decimals)).toLocaleString() : 'N/A'}
            </p>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Exchange Supply</h3>
            <p className="text-3xl font-bold text-white">
              {metrics.exchange_supply && metrics.decimals ? 
                (metrics.exchange_supply / Math.pow(10, metrics.decimals)).toLocaleString() : 'N/A'}
            </p>
            {metrics.exchange_supply_percentage && (
              <p className="text-sm text-gray-400 mt-1">
                ({metrics.exchange_supply_percentage.toFixed(2)}% of total)
              </p>
            )}
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Current Price</h3>
            <p className="text-3xl font-bold text-white">
              {metrics.current_price ? `$${metrics.current_price.toFixed(6)}` : 'N/A'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
            <h3 className="text-gray-300 text-lg mb-2">Market Cap per Holder</h3>
            <p className="text-3xl font-bold text-white">
              {metrics.current_price && metrics.total_supply && metrics.total_holders && metrics.decimals ? 
                `$${((metrics.current_price * (metrics.total_supply / Math.pow(10, metrics.decimals))) / metrics.total_holders).toLocaleString()}` : 'N/A'}
            </p>
          </div>
        </div>

        {/* Co-owned Tokens */}
        {metrics.co_ownership && metrics.co_ownership.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-300">Top Co-owned Tokens</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {metrics.co_ownership.map((coin, index) => (
                <Link to={`/token/${coin.mint}`} key={coin.mint}>
                  <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:bg-gray-700 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3 mb-4">
                      <TokenAvatar 
                        imageUrl={coin.image_url}
                        symbol={coin.symbol || coin.mint}
                        size="sm"
                        className="w-8 h-8"
                      />
                      <div>
                        <h3 className="text-gray-300 text-lg">#{index + 1} {coin.name || coin.symbol || coin.mint}</h3>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-white">{coin.percentage.toFixed(2)}%</p>
                    <p className="text-sm text-gray-400">of holders also own this token</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-300">Holder Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $10</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_10?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_10_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $100</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_100?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_100_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $1,000</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_1000?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_1000_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $10,000</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_10000?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_10000_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $100,000</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_100000?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_100000_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Holders &gt; $1M</h3>
              <p className="text-2xl font-bold text-white">
                {metrics?.holder_counts_by_usd?.above_1000000?.toLocaleString() ?? 'N/A'}
                <span className="text-sm text-gray-400 ml-2">
                  ({metrics?.holder_counts_by_usd?.above_1000000_percent?.toFixed(2) ?? 'N/A'}%)
                </span>
              </p>
            </div>
          </div>
        </div>


        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-300">Holder Distribution</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(metrics.holder_distribution).map(([key, value]) => (
              <div key={key} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
                <h3 className="text-gray-300 text-lg mb-2">{key.replace(/_/g, ' ').toUpperCase()}</h3>
                <p className="text-2xl font-bold text-white">{value.toFixed(2)}%</p>
              </div>
            ))}
          </div>
        </div>

        {/* Lorenz Curve */}
        {metrics.lorenz_curve && metrics.lorenz_curve.holders && metrics.lorenz_curve.holdings && metrics.lorenz_curve.holders.length > 0 && metrics.lorenz_curve.holdings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-300">Lorenz Curve</h2>
            <div className="h-96 bg-gray-800 rounded-lg p-4" style={{ height: '384px', backgroundColor: '#1f2937', borderRadius: '8px', padding: '16px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={metrics.lorenz_curve.holders.map((holder: number, index: number) => ({
                    holders: holder,
                    holdings: metrics.lorenz_curve.holdings[index] || 0,
                    perfect: holder // This will create the perfect equality line
                  }))}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="holders"
                    type="number"
                    domain={[0, 100]}
                    ticks={[0, 20, 40, 60, 80, 100]}
                    stroke="#9CA3AF"
                    label={{ value: 'Cumulative % of Holders', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }}
                  />
                  <YAxis
                    type="number"
                    domain={[0, 100]}
                    stroke="#9CA3AF"
                    label={{ value: 'Cumulative % of Holdings', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    formatter={(value: number) => `${value.toFixed(2)}%`}
                    labelFormatter={(value: number) => `${value.toFixed(2)}% of Holders`}
                  />
                  <Legend wrapperStyle={{ color: '#9CA3AF' }} />
                  <Line
                    type="monotone"
                    dataKey="holdings"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    name="Lorenz Curve"
                    dot={false}
                  />
                  <Line
                    type="linear"
                    dataKey="perfect"
                    stroke="#10B981"
                    strokeWidth={2}
                    name="Perfect Equality"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Price History */}
        {metrics.price_history && metrics.price_history.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4 text-gray-300">Price History</h2>
            <div className="h-96 bg-gray-800 rounded-lg p-4" style={{ height: '384px', backgroundColor: '#1f2937', borderRadius: '8px', padding: '16px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart 
                  data={metrics.price_history}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="timestamp"
                    stroke="#9CA3AF"
                    tickFormatter={(timestamp) => new Date(timestamp).toLocaleDateString()}
                    label={{ value: 'Date', position: 'insideBottom', offset: -5, fill: '#9CA3AF' }}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    label={{ value: 'Price (USD)', angle: -90, position: 'insideLeft', fill: '#9CA3AF' }}
                  />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1F2937', border: 'none' }}
                    formatter={(value: number) => [`${value.toFixed(4)}`, 'Price']}
                    labelFormatter={(timestamp) => new Date(timestamp).toLocaleString()}
                  />
                  <Legend wrapperStyle={{ color: '#9CA3AF' }} />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    name="Price"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Top Holders Table */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-300">Top Holders</h2>
          <div className="overflow-x-auto rounded-lg bg-gray-800">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-700">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-700">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-700">Delegated</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider border-b border-gray-700">Percentage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {metrics?.top_holders?.map((holder: any, index: number) => {
                  
                  // Calculate percentage - try different approaches
                  const calculatePercentage = () => {
                    // First check if holder already has percentage
                    if (holder.percentage !== undefined && holder.percentage !== null) {
                      return holder.percentage;
                    }
                    
                    // If no decimals info, assume percentage should be calculated differently
                    // Many tokens store amounts already adjusted for decimals
                    if (!metrics.decimals || !metrics.total_supply) {
                      // Fallback: calculate percentage relative to sum of all holders
                      const totalHolderAmount = metrics.top_holders?.reduce((sum: number, h: any) => sum + (h.amount || 0), 0) || 1;
                      return (holder.amount / totalHolderAmount) * 100;
                    }
                    
                    // Standard calculation with decimal adjustment
                    const adjustedAmount = holder.amount / Math.pow(10, metrics.decimals);
                    const adjustedTotalSupply = metrics.total_supply / Math.pow(10, metrics.decimals);
                    return (adjustedAmount / adjustedTotalSupply) * 100;
                  };
                  
                  const typedHolder: Holder = {
                    address: holder.address,
                    owner: holder.owner,
                    amount: holder.amount,
                    delegated_amount: holder.delegated_amount,
                    total_amount: holder.total_amount,
                    label: holder.label,
                    is_exchange: holder.is_exchange,
                    percentage: calculatePercentage()
                  };
                  return (
                    <tr key={typedHolder.address} className="hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-400">{index + 1}.</span>
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://solscan.io/account/${typedHolder.address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-400 hover:text-blue-300"
                            >
                              {typedHolder.label ? 
                                `${typedHolder.label} (${typedHolder.address})` : 
                                typedHolder.address
                              }
                            </a>
                            {typedHolder.is_exchange && (
                              <span className="px-2 py-0.5 text-xs bg-yellow-500/20 text-yellow-400 rounded">
                                Exchange
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {typedHolder.amount.toLocaleString()}
                        {typedHolder.delegated_amount && (
                          <div className="text-xs text-gray-400">
                            +{typedHolder.delegated_amount.toLocaleString()} delegated
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {typedHolder.delegated_amount?.toLocaleString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {typedHolder.percentage.toFixed(2)}%
                        {typedHolder.total_amount && (
                          <div className="text-xs text-gray-400">
                            {/* Show total amount if different from regular amount */}
                            {typedHolder.total_amount !== typedHolder.amount && (
                              `(${typedHolder.total_amount.toLocaleString()} total)`
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Market Cap per Holder Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">Market Cap per Holder Categories</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $10</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_10 ? 
                  `$${metrics.market_cap_per_holder.above_10.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $100</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_100 ? 
                  `$${metrics.market_cap_per_holder.above_100.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $1,000</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_1000 ? 
                  `$${metrics.market_cap_per_holder.above_1000.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $10,000</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_10000 ? 
                  `$${metrics.market_cap_per_holder.above_10000.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $100,000</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_100000 ? 
                  `$${metrics.market_cap_per_holder.above_100000.toLocaleString()}` : 'N/A'}
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
              <h3 className="text-gray-300 text-lg mb-2">Market Cap / Holders &gt; $1M</h3>
              <p className="text-3xl font-bold text-white">
                {metrics?.market_cap_per_holder?.above_1000000 ? 
                  `$${metrics.market_cap_per_holder.above_1000000.toLocaleString()}` : 'N/A'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 